/**
 * AI Art Director — the critique rules.
 *
 * Each rule is data: which review areas it covers, which detection it makes,
 * which scores it costs, how severe it is, and a pure `check` over the review
 * context (Creative Plan + Research Brief + selected patterns + genome +
 * constraints). Nothing here touches canvas, pixels, geometry, Fabric, the
 * editor, the compiler, or objects — the Art Director reviews CREATIVE quality.
 *
 * A rule may also state a `strength` — what is genuinely good when it passes —
 * so a review credits the work rather than only faulting it.
 */

import {
  SEVERITY, LANGUAGE_COHERENCE, MEDIUM_COHERENCE, COMPOSITION_LIMITS,
  DERIVATIVE_SIMILARITY, QUIET_CTA,
} from './reviewSchema';

/* ---------------------------------------------------------------- helpers */

const choice = (plan, key) => (plan && plan[key] ? plan[key].choice : undefined);
const listChoice = (plan, key) => { const c = choice(plan, key); return Array.isArray(c) ? c : []; };
const list = (v) => (Array.isArray(v) ? v : []);
const humanize = (v) => String(v || '').replace(/-/g, ' ');
const count = (n, noun) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** Which genome dimensions a plan has drifted away from. */
const drift = (plan, genome, map) =>
  Object.entries(map)
    .filter(([planKey, dim]) => genome[dim] !== undefined && choice(plan, planKey) !== undefined && choice(plan, planKey) !== genome[dim])
    .map(([planKey, dim]) => ({ planKey, dim, planned: choice(plan, planKey), language: genome[dim] }));

/** Selected patterns that do not belong to the chosen genome's language. */
const offLanguagePatterns = (patterns, genome) =>
  patterns.filter((p) => Array.isArray(p.idealGenomes) && genome.id && !p.idealGenomes.includes(genome.id));

/** Incompatible pairs inside the selected set, from the patterns' own relationships. */
const patternConflicts = (patterns) => {
  const ids = new Set(patterns.map((p) => p.id));
  const pairs = [];
  patterns.forEach((p) => {
    list(p.incompatiblePatterns).forEach((other) => {
      if (!ids.has(other)) return;
      const pair = [p.id, other].sort();
      if (!pairs.some((x) => x[0] === pair[0] && x[1] === pair[1])) pairs.push(pair);
    });
  });
  return pairs;
};

const isOfferLed = (plan) => listChoice(plan, 'hierarchyStrategy')[0] === 'offer';

/* ------------------------------------------------------------------ rules */

export const RULES = Object.freeze([
  {
    id: 'weak-hierarchy',
    reviews: ['Hierarchy Strategy'],
    detects: 'Weak hierarchy',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'communication', 'storytelling'],
    check: (ctx) => {
      const roles = listChoice(ctx.plan, 'hierarchyStrategy');
      return { issue: roles.length < 3, detail: roles.join(' → ') };
    },
    weakness: () => 'Hierarchy is too flat to guide the eye — fewer than three ranked roles means nothing clearly leads.',
    revision: () => 'Rank at least three roles, with one unmistakable focal point and a clear step down to support and action.',
    strength: (ctx) => {
      const roles = listChoice(ctx.plan, 'hierarchyStrategy');
      return roles.length >= 4 ? `Hierarchy is decisive across ${count(roles.length, 'level')}: ${roles.join(' → ')}.` : null;
    },
  },
  {
    id: 'weak-marketing-message',
    reviews: ['Marketing Intent', 'Business Intent'],
    detects: 'Weak marketing message',
    severity: SEVERITY.MAJOR,
    scores: ['marketing', 'communication'],
    check: (ctx) => ({
      issue: !choice(ctx.plan, 'marketingIntent') || list(ctx.research.marketingPsychology).length === 0,
    }),
    weakness: () => 'The marketing message has no strategic backing — no marketing psychology supports why this should persuade anyone.',
    revision: () => 'Ground the marketing intent in a named persuasion driver for this industry and audience, and let it lead the hierarchy.',
    strength: (ctx) => {
      const drivers = list(ctx.research.marketingPsychology);
      return drivers.length >= 2
        ? `Marketing intent (${humanize(choice(ctx.plan, 'marketingIntent'))}) is backed by ${count(drivers.length, 'persuasion driver')}.`
        : null;
    },
  },
  {
    id: 'weak-cta',
    reviews: ['CTA Strategy'],
    detects: 'Weak CTA',
    severity: SEVERITY.MAJOR,
    scores: ['marketing', 'marketplace'],
    check: (ctx) => {
      const cta = choice(ctx.plan, 'ctaStrategy');
      if (!cta) return { issue: true, detail: 'none' };
      // A promotion whispering its action wastes the campaign.
      return { issue: isOfferLed(ctx.plan) && QUIET_CTA.includes(cta), detail: cta };
    },
    weakness: (ctx, detail) => (detail === 'none'
      ? 'There is no call to action — the design asks the viewer to do nothing.'
      : `This is an offer-led design, but the call to action is a ${humanize(detail)} — far too quiet to convert.`),
    revision: (ctx, detail) => (detail === 'none'
      ? 'Commit to one primary action and give it a treatment that owns the end of the reading flow.'
      : `Promote the ${humanize(detail)} to a high-contrast, unmistakable action; an offer must be answerable at a glance.`),
    strength: (ctx) => {
      const cta = choice(ctx.plan, 'ctaStrategy');
      return cta && !QUIET_CTA.includes(cta) ? `The ${humanize(cta)} call to action carries the campaign with conviction.` : null;
    },
  },
  {
    id: 'weak-storytelling',
    reviews: ['Visual Narrative', 'Communication Strategy'],
    detects: 'Weak storytelling',
    severity: SEVERITY.MAJOR,
    scores: ['storytelling', 'communication'],
    check: (ctx) => ({
      issue: !choice(ctx.plan, 'visualNarrative') || !choice(ctx.plan, 'communicationStrategy'),
    }),
    weakness: () => 'There is no visual narrative — the design presents information instead of telling a story.',
    revision: () => 'State how the eye should travel and what it should feel at each beat, from entry point to action.',
    strength: (ctx) => (choice(ctx.plan, 'visualNarrative')
      ? `The narrative carries a deliberate arc: ${humanize(choice(ctx.plan, 'visualNarrative'))}.` : null),
  },
  {
    id: 'weak-emotional-direction',
    reviews: ['Communication Strategy'],
    detects: 'Weak emotional direction',
    severity: SEVERITY.MINOR,
    scores: ['creative', 'communication'],
    check: (ctx) => ({ issue: !choice(ctx.plan, 'creativeDirection') || !choice(ctx.plan, 'visualLanguage') }),
    weakness: () => 'The emotional direction is undefined — the design has no stated feeling to aim for.',
    revision: () => 'Name the emotional register the work must land, and tie the visual language to it.',
    strength: null,
  },
  {
    id: 'weak-premium-feel',
    reviews: ['Premium Cues'],
    detects: 'Weak premium feeling',
    severity: SEVERITY.MAJOR,
    scores: ['premium', 'marketplace'],
    check: (ctx) => {
      const cues = listChoice(ctx.plan, 'premiumCues');
      return { issue: cues.length < COMPOSITION_LIMITS.minPremiumCues, detail: String(cues.length) };
    },
    weakness: (ctx, detail) => `Only ${count(Number(detail), 'premium cue')} — the work will read as competent, not crafted.`,
    revision: () => `Signal quality through at least ${COMPOSITION_LIMITS.minPremiumCues} distinct cues (restraint, refined type, considered colour, crafted detail) rather than decoration.`,
    strength: (ctx) => {
      const cues = listChoice(ctx.plan, 'premiumCues');
      return cues.length >= 4 ? `Premium is earned through ${count(cues.length, 'cue')}: ${cues.map(humanize).join(', ')}.` : null;
    },
  },
  {
    id: 'weak-visual-language',
    reviews: ['Visual Language', 'Composition Strategy', 'Typography Strategy', 'Color Strategy'],
    detects: 'Weak visual language',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'premium'],
    check: (ctx) => {
      const drifted = drift(ctx.plan, ctx.genome, LANGUAGE_COHERENCE);
      return { issue: drifted.length > 0, detail: drifted };
    },
    weakness: (ctx, detail) => `The plan abandons its own ${ctx.genome.name} language: ${detail.map((d) => `${d.dim} is ${humanize(d.planned)} where the language calls for ${humanize(d.language)}`).join('; ')}.`,
    revision: (ctx, detail) => `Return ${detail.map((d) => d.dim).join(', ')} to the ${ctx.genome.name} language, or change the genome deliberately — a design cannot speak two languages at once.`,
    strength: (ctx) => `Composition, type and colour all speak one ${ctx.genome.name} language.`,
  },
  {
    id: 'incoherent-medium',
    reviews: ['Photography Strategy', 'Illustration Strategy', 'Shape Strategy', 'Decoration Strategy'],
    detects: 'Weak visual language',
    severity: SEVERITY.MINOR,
    scores: ['creative', 'premium'],
    check: (ctx) => {
      const drifted = drift(ctx.plan, ctx.genome, MEDIUM_COHERENCE);
      return { issue: drifted.length > 0, detail: drifted };
    },
    weakness: (ctx, detail) => `Supporting media drift from the ${ctx.genome.name} language: ${detail.map((d) => `${d.dim} is ${humanize(d.planned)}, not ${humanize(d.language)}`).join('; ')}.`,
    revision: (ctx, detail) => `Bring ${detail.map((d) => d.dim).join(', ')} back into the ${ctx.genome.name} language so imagery, shape and decoration reinforce rather than dilute it.`,
    strength: null,
  },
  {
    id: 'wrong-genome',
    reviews: ['Style Genome'],
    detects: 'Wrong genome',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'marketing', 'marketplace'],
    check: (ctx) => {
      const recommended = list(ctx.research.recommendedGenomes);
      return { issue: recommended.length > 0 && Boolean(ctx.genome.id) && !recommended.includes(ctx.genome.id), detail: recommended };
    },
    weakness: (ctx, detail) => `The ${ctx.genome.name} language is not what this market expects — research points to ${detail.map(humanize).join(', ')}.`,
    revision: (ctx, detail) => `Re-language the concept around ${humanize(detail[0])}, or justify why departing from the category convention serves this brief.`,
    strength: (ctx) => (list(ctx.research.recommendedGenomes).includes(ctx.genome.id)
      ? `The ${ctx.genome.name} language is exactly what research recommends for this market.` : null),
  },
  {
    id: 'wrong-pattern-selection',
    reviews: ['Pattern Selection'],
    detects: 'Wrong pattern selection',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'marketplace'],
    check: (ctx) => {
      const off = offLanguagePatterns(ctx.patterns, ctx.genome);
      return { issue: off.length > 0, detail: off.map((p) => p.name || p.id) };
    },
    weakness: (ctx, detail) => `${detail.join(', ')} ${detail.length === 1 ? 'does' : 'do'} not belong to the ${ctx.genome.name} language and will fight the concept.`,
    revision: (ctx, detail) => `Replace ${detail.join(', ')} with patterns whose design philosophy suits ${ctx.genome.name}.`,
    strength: (ctx) => (ctx.patterns.length > 0 && offLanguagePatterns(ctx.patterns, ctx.genome).length === 0
      ? `All ${count(ctx.patterns.length, 'selected pattern')} suit the ${ctx.genome.name} language.` : null),
  },
  {
    id: 'conflicting-patterns',
    reviews: ['Pattern Selection'],
    detects: 'Wrong pattern selection',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'marketplace'],
    check: (ctx) => {
      const pairs = patternConflicts(ctx.patterns);
      return { issue: pairs.length > 0, detail: pairs };
    },
    weakness: (ctx, detail) => `Conflicting patterns selected together: ${detail.map((p) => p.join(' + ')).join('; ')} — they contradict each other's composition.`,
    revision: (ctx, detail) => `Drop one side of ${detail.map((p) => p.join(' / ')).join('; ')}; a composition cannot honour both.`,
    strength: null,
  },
  {
    id: 'too-much-complexity',
    reviews: ['Composition Strategy', 'Pattern Selection'],
    detects: 'Too much complexity',
    severity: SEVERITY.MINOR,
    scores: ['creative', 'communication', 'premium'],
    check: (ctx) => {
      const priorities = listChoice(ctx.plan, 'contentPriorities');
      const tooMany = ctx.patterns.length > COMPOSITION_LIMITS.maxPatterns;
      const tooDense = priorities.length > COMPOSITION_LIMITS.maxPriorities;
      return { issue: tooMany || tooDense, detail: { patterns: ctx.patterns.length, priorities: priorities.length } };
    },
    weakness: (ctx, detail) => `The concept is overloaded — ${count(detail.patterns, 'pattern')} and ${count(detail.priorities, 'content block')} competing for one surface.`,
    revision: () => `Cut to the few blocks that carry the message; anything that does not earn its place dilutes the ones that do.`,
    strength: null,
  },
  {
    id: 'too-much-simplicity',
    reviews: ['Pattern Selection', 'Marketplace Readiness'],
    detects: 'Too much simplicity',
    severity: SEVERITY.MINOR,
    scores: ['marketplace', 'creative'],
    check: (ctx) => {
      const priorities = listChoice(ctx.plan, 'contentPriorities');
      const thin = ctx.patterns.length < COMPOSITION_LIMITS.minPatterns;
      const bare = priorities.length < COMPOSITION_LIMITS.minPriorities;
      return { issue: thin || bare, detail: { patterns: ctx.patterns.length, priorities: priorities.length } };
    },
    weakness: (ctx, detail) => `The concept is too thin to sell — ${count(detail.patterns, 'pattern')} and ${count(detail.priorities, 'content block')} give a buyer little to adapt.`,
    revision: () => 'Add the structure a buyer expects for this document — restraint is not the same as emptiness.',
    strength: null,
  },
  {
    id: 'weak-differentiation',
    reviews: ['Diversity'],
    detects: 'Weak differentiation',
    severity: SEVERITY.BLOCKER,
    scores: ['originality', 'marketplace'],
    check: (ctx) => {
      const duplicate = Boolean(ctx.constraints.diversityConstraints && ctx.constraints.diversityConstraints.duplicate);
      const swapped = Boolean(ctx.plan.meta && ctx.plan.meta.genomeSwapped);
      // A duplicate is only forgivable if the plan changed its language in response.
      return { issue: duplicate && !swapped };
    },
    weakness: () => 'This repeats a design that already exists, and the plan did not change its language in response — it adds nothing to the catalog.',
    revision: () => 'Change the visual language itself, not the details: a different genome, a different composition, a different narrative.',
    strength: (ctx) => (ctx.constraints.diversityConstraints && !ctx.constraints.diversityConstraints.duplicate
      ? 'The concept is distinct from everything in memory.' : null),
  },
  {
    id: 'weak-originality',
    reviews: ['Diversity'],
    detects: 'Weak originality',
    severity: SEVERITY.MINOR,
    scores: ['originality', 'marketplace'],
    check: (ctx) => {
      const max = (ctx.constraints.diversityConstraints && ctx.constraints.diversityConstraints.maxSimilarity) || 0;
      const duplicate = Boolean(ctx.constraints.diversityConstraints && ctx.constraints.diversityConstraints.duplicate);
      // Derivative but not a duplicate — the idea is recognisable, not fresh.
      return { issue: !duplicate && max >= DERIVATIVE_SIMILARITY, detail: max };
    },
    weakness: (ctx, detail) => `The concept is derivative — it resembles existing work (${Math.round(detail * 100)}% similar) without a distinct idea of its own.`,
    revision: (ctx) => `Push the axes memory flagged: ${list(ctx.constraints.generationConstraints && ctx.constraints.generationConstraints.recommendedVariationAxes).join(', ') || 'genome, composition, narrative'}.`,
    strength: (ctx) => {
      const max = (ctx.constraints.diversityConstraints && ctx.constraints.diversityConstraints.maxSimilarity) || 0;
      return ctx.constraints.diversityConstraints && max === 0 ? 'Nothing comparable exists in memory — the idea is fresh.' : null;
    },
  },
  {
    id: 'memory-constraint-violated',
    reviews: ['Memory Constraints'],
    detects: 'Weak differentiation',
    severity: SEVERITY.BLOCKER,
    scores: ['originality', 'marketplace'],
    check: (ctx) => {
      const avoid = (ctx.constraints.generationConstraints && ctx.constraints.generationConstraints.avoid) || {};
      const violated = Object.entries(avoid).filter(([dim, value]) => {
        if (Array.isArray(value)) return false;                  // list values mean "vary", not "avoid this exact thing"
        if (dim === 'genome') return ctx.genome.id === value;
        return false;
      });
      return { issue: violated.length > 0, detail: violated.map(([dim, value]) => `${dim}: ${value}`) };
    },
    weakness: (ctx, detail) => `The plan uses exactly what memory told it to avoid (${detail.join(', ')}).`,
    revision: () => 'Honour the memory constraints: the flagged choices are already represented in the catalog.',
    strength: null,
  },
  {
    id: 'weak-marketplace-value',
    reviews: ['Marketplace Readiness'],
    detects: 'Weak marketplace value',
    severity: SEVERITY.MAJOR,
    scores: ['marketplace', 'premium'],
    check: (ctx) => {
      const reasons = [];
      if (ctx.patterns.length === 0) reasons.push('no design patterns behind it');
      if (listChoice(ctx.plan, 'premiumCues').length < 2) reasons.push('almost no premium signal');
      if (!choice(ctx.plan, 'ctaStrategy')) reasons.push('no call to action');
      return { issue: reasons.length > 0, detail: reasons };
    },
    weakness: (ctx, detail) => `This would not earn a place in the marketplace: ${detail.join(', ')}.`,
    revision: () => 'A sellable template needs real structure, a visible sense of craft, and a clear action — establish all three.',
    strength: null,
  },
]);
